'use client';

import { ChevronLeft, ChevronRight, FileInput, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { DataQualityTierBadge } from './DataQualitySelect';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getActivityCategoryLabel } from '@/features/ghg/lib/category-labels';
import type {
  ActivityDataListItem,
  ActivityDataSortBy,
  ActivityDataStatus,
  SortOrder,
} from '@/features/ghg/types/activity-data';
import { useTranslation } from 'react-i18next';

const STATUS_BADGE_VARIANTS: Record<
  ActivityDataStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  draft: 'secondary',
  submitted: 'outline',
  pending_approval: 'default',
  approved: 'default',
  rejected: 'destructive',
};

function getSortOptions(t: (key: string) => string): { value: ActivityDataSortBy; label: string }[] {
  return [
    { value: 'createdAt', label: t('scopeCalculationsEntriesCard.sort_created_at') },
    { value: 'estimatedCo2eKg', label: t('scopeCalculationsEntriesCard.sort_estimated_emissions') },
    { value: 'quantity', label: t('scopeCalculationsEntriesCard.sort_quantity') },
    { value: 'facilityName', label: t('scopeCalculationsEntriesCard.sort_facility') },
    { value: 'activityCategory', label: t('scopeCalculationsEntriesCard.sort_category') },
    { value: 'activityType', label: t('scopeCalculationsEntriesCard.sort_activity_type') },
    { value: 'dataQualityScore', label: t('scopeCalculationsEntriesCard.sort_data_quality_score') },
    { value: 'status', label: t('scopeCalculationsEntriesCard.sort_status') },
  ];
}

interface ScopeCalculationsEntriesCardSearch {
  facilityId?: string | undefined;
  activityGroupId?: string | undefined;
  status?: ActivityDataStatus | undefined;
  dataQuality?: 'measured' | 'calculated' | 'estimated' | undefined;
  missingEmissions?: boolean | undefined;
}

interface ScopeCalculationsEntriesCardProps {
  scopeLabel: string;
  periodName?: string | undefined;
  search: ScopeCalculationsEntriesCardSearch;
  facilities: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  currentSortBy: ActivityDataSortBy;
  currentSortOrder: SortOrder;
  onFilterChange: (patch: Record<string, unknown>) => void;
  onPageChange: (page: number) => void;
  extraFilters?: ReactNode;
  entries: ActivityDataListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  deletePending: boolean;
  onDeleteSelected: (entryIds: string[]) => Promise<void>;
  onRowClick: (entry: ActivityDataListItem) => void;
  noEntriesTitle: string;
  noEntriesDescription: string;
}

function toTitleCase(raw: string): string {
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getStatusLabel(status: ActivityDataStatus, t: (key: string) => string): string {
  const labels: Record<ActivityDataStatus, string> = {
    draft: t('scopeCalculationsEntriesCard.status_draft'),
    submitted: t('scopeCalculationsEntriesCard.status_submitted'),
    pending_approval: t('scopeCalculationsEntriesCard.status_pending'),
    approved: t('scopeCalculationsEntriesCard.status_approved'),
    rejected: t('scopeCalculationsEntriesCard.status_rejected'),
  };
  return labels[status];
}

function formatQuantity(quantity: number, unit: string): string {
  return `${quantity.toLocaleString()} ${unit}`;
}

function formatEmissions(estimatedCo2eKg: number | null | undefined): string {
  if (estimatedCo2eKg == null) return '-';
  if (estimatedCo2eKg >= 1000) {
    return `${(estimatedCo2eKg / 1000).toLocaleString(undefined, {
      maximumFractionDigits: 3,
    })} tCO2e`;
  }
  return `${estimatedCo2eKg.toLocaleString(undefined, { maximumFractionDigits: 3 })} kg CO2e`;
}

function getActivityLabel(entry: ActivityDataListItem): string {
  if (entry.activityGroups && entry.activityGroups.length > 0) {
    if (entry.activityGroups.length === 1) {
      return entry.activityGroups[0]?.name ?? 'Activity Group';
    }
    const first = entry.activityGroups[0]?.name ?? 'Activity Group';
    return `${first} +${String(entry.activityGroups.length - 1)} more`;
  }
  if (entry.activityGroup?.name) return entry.activityGroup.name;
  if (entry.activityDisplayName?.trim()) return entry.activityDisplayName;
  if (entry.activityType.startsWith('catalog:')) return 'Catalog Activity';
  if (entry.activityType.trim()) return toTitleCase(entry.activityType);
  return entry.scope3Category != null
    ? getActivityCategoryLabel(entry.activityCategory, undefined, {
        scope3Category: entry.scope3Category,
      })
    : toTitleCase(entry.activityCategory);
}

export function ScopeCalculationsEntriesCard({
  scopeLabel,
  periodName,
  search,
  facilities,
  groups,
  currentSortBy,
  currentSortOrder,
  onFilterChange,
  onPageChange,
  extraFilters,
  entries,
  total,
  page,
  pageSize,
  totalPages,
  isLoading,
  isError,
  error,
  deletePending,
  onDeleteSelected,
  onRowClick,
  noEntriesTitle,
  noEntriesDescription,
}: ScopeCalculationsEntriesCardProps) {
  const { t } = useTranslation('ghg');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedEntryIds, setSelectedEntryIds] = useState<string[]>([]);
  const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);

  const selectableEntryIds = useMemo(
    () => entries.filter((entry) => entry.status !== 'approved').map((entry) => entry.id),
    [entries]
  );
  const selectedOnPageCount = useMemo(
    () => selectedEntryIds.filter((id) => selectableEntryIds.includes(id)).length,
    [selectedEntryIds, selectableEntryIds]
  );
  const allSelectableOnPageSelected =
    selectableEntryIds.length > 0 && selectedOnPageCount === selectableEntryIds.length;
  const hasPartialPageSelection =
    selectedOnPageCount > 0 && selectedOnPageCount < selectableEntryIds.length;
  const selectedEntryNoun = selectedOnPageCount === 1 ? t('scopeCalculationsEntriesCard.noun_entry') : t('scopeCalculationsEntriesCard.noun_entries');

  useEffect(() => {
    setSelectedEntryIds((prev) => prev.filter((id) => entries.some((entry) => entry.id === id)));
  }, [entries]);

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => {
      if (prev) {
        setSelectedEntryIds([]);
      }
      return !prev;
    });
  };

  const toggleEntrySelection = (entry: ActivityDataListItem, checked: boolean) => {
    if (entry.status === 'approved') return;
    setSelectedEntryIds((prev) => {
      if (checked) {
        if (prev.includes(entry.id)) return prev;
        return [...prev, entry.id];
      }
      return prev.filter((id) => id !== entry.id);
    });
  };

  const toggleSelectAllCurrentPage = (checked: boolean) => {
    setSelectedEntryIds((prev) => {
      if (checked) {
        const merged = new Set([...prev, ...selectableEntryIds]);
        return Array.from(merged);
      }
      return prev.filter((id) => !selectableEntryIds.includes(id));
    });
  };

  const handleBulkDelete = async () => {
    const idsToDelete = selectedEntryIds.filter((id) => selectableEntryIds.includes(id));
    if (idsToDelete.length === 0) {
      setConfirmBulkDeleteOpen(false);
      return;
    }

    try {
      await onDeleteSelected(idsToDelete);
      setConfirmBulkDeleteOpen(false);
      setSelectedEntryIds([]);
      setSelectionMode(false);
      toast.success(
        t('scopeCalculationsEntriesCard.toast_deleted', { count: idsToDelete.length, scope: scopeLabel })
      );
    } catch (deleteError) {
      toast.error(
        deleteError instanceof Error ? deleteError.message : t('scopeCalculationsEntriesCard.toast_delete_failed')
      );
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>{t('scopeCalculationsEntriesCard.cardTitle_entries')}</CardTitle>
            <CardDescription>
              {t('scopeCalculationsEntriesCard.cardDesc_live_entries', { scope: scopeLabel, period: periodName ?? t('scopeCalculationsEntriesCard.selected_period') })}
            </CardDescription>
          </div>
          <Button
            variant={selectionMode ? 'secondary' : 'outline'}
            size="sm"
            onClick={toggleSelectionMode}
            className="w-full sm:w-auto"
          >
            {selectionMode ? t('scopeCalculationsEntriesCard.btn_cancel_select') : t('scopeCalculationsEntriesCard.btn_select')}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4 xl:grid-cols-8">
            <Select
              options={[
                { value: '', label: t('scopeCalculationsEntriesCard.filter_all_facilities') },
                ...facilities.map((facility) => ({ value: facility.id, label: facility.name })),
              ]}
              value={search.facilityId ?? ''}
              onChange={(value) => {
                onFilterChange({ facilityId: value || undefined });
              }}
              className="w-full"
            />
            <Select
              options={[
                { value: '', label: t('scopeCalculationsEntriesCard.filter_all_groups') },
                ...groups.map((group) => ({ value: group.id, label: group.name })),
              ]}
              value={search.activityGroupId ?? ''}
              onChange={(value) => {
                onFilterChange({ activityGroupId: value || undefined });
              }}
              className="w-full"
            />
            {extraFilters}
            <Select
              options={[
                { value: '', label: t('scopeCalculationsEntriesCard.filter_all_statuses') },
                { value: 'draft', label: t('scopeCalculationsEntriesCard.status_draft') },
                { value: 'submitted', label: t('scopeCalculationsEntriesCard.status_submitted') },
                { value: 'pending_approval', label: t('scopeCalculationsEntriesCard.status_pending_approval') },
                { value: 'approved', label: t('scopeCalculationsEntriesCard.status_approved') },
                { value: 'rejected', label: t('scopeCalculationsEntriesCard.status_rejected') },
              ]}
              value={search.status ?? ''}
              onChange={(value) => {
                onFilterChange({ status: value || undefined });
              }}
              className="w-full"
            />
            <Select
              options={[
                { value: '', label: t('scopeCalculationsEntriesCard.filter_all_quality') },
                { value: 'measured', label: t('scopeCalculationsEntriesCard.quality_measured') },
                { value: 'calculated', label: t('scopeCalculationsEntriesCard.quality_calculated') },
                { value: 'estimated', label: t('scopeCalculationsEntriesCard.quality_estimated') },
              ]}
              value={search.dataQuality ?? ''}
              onChange={(value) => {
                onFilterChange({ dataQuality: value || undefined });
              }}
              className="w-full"
            />
            <Select
              options={[
                { value: '', label: t('scopeCalculationsEntriesCard.filter_all_emissions_states') },
                { value: 'false', label: t('scopeCalculationsEntriesCard.filter_has_estimate') },
                { value: 'true', label: t('scopeCalculationsEntriesCard.filter_missing_estimate') },
              ]}
              value={search.missingEmissions === undefined ? '' : String(search.missingEmissions)}
              onChange={(value) => {
                onFilterChange({
                  missingEmissions: value === '' ? undefined : value === 'true',
                });
              }}
              className="w-full"
            />
            <Select
              options={getSortOptions(t)}
              value={currentSortBy}
              onChange={(value) => {
                onFilterChange({ sortBy: value as ActivityDataSortBy });
              }}
              className="w-full"
            />
            <Select
              options={[
                { value: 'desc', label: t('scopeCalculationsEntriesCard.sort_desc') },
                { value: 'asc', label: t('scopeCalculationsEntriesCard.sort_asc') },
              ]}
              value={currentSortOrder}
              onChange={(value) => {
                onFilterChange({ sortOrder: value as SortOrder });
              }}
              className="w-full"
            />
          </div>

          {isError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
              {t('scopeCalculationsEntriesCard.error_load_entries')}: {error?.message}
            </div>
          )}

          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center">
              <FileInput className="mb-3 h-10 w-10 text-muted-foreground" />
              <p className="text-lg font-medium">{noEntriesTitle}</p>
              <p className="mt-1 text-sm text-muted-foreground">{noEntriesDescription}</p>
            </div>
          ) : (
            <>
              {selectionMode && (
                <div className="flex flex-col gap-3 rounded-md border bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    {selectableEntryIds.length === 0
                      ? t('scopeCalculationsEntriesCard.no_deletable_entries')
                      : t('scopeCalculationsEntriesCard.selected_on_page', { count: selectedOnPageCount })}
                  </p>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={selectedOnPageCount === 0 || deletePending}
                    onClick={() => {
                      setConfirmBulkDeleteOpen(true);
                    }}
                  >
                    {deletePending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('scopeCalculationsEntriesCard.btn_delete_selected')}
                  </Button>
                </div>
              )}

              <div className="hidden rounded-md border md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {selectionMode && (
                        <TableHead className="w-12">
                          <Checkbox
                            checked={
                              allSelectableOnPageSelected
                                ? true
                                : hasPartialPageSelection
                                  ? 'indeterminate'
                                  : false
                            }
                            onCheckedChange={(checked) => {
                              toggleSelectAllCurrentPage(Boolean(checked));
                            }}
                            disabled={selectableEntryIds.length === 0}
                            title={
                              selectableEntryIds.length === 0
                                ? t('scopeCalculationsEntriesCard.no_deletable_entries_short')
                                : undefined
                            }
                            aria-label={t('scopeCalculationsEntriesCard.ariaLabel_select_all_entries_on_this_page')}
                          />
                        </TableHead>
                      )}
                      <TableHead>{t('scopeCalculationsEntriesCard.th_activity')}</TableHead>
                      <TableHead>{t('scopeCalculationsEntriesCard.th_facility')}</TableHead>
                      <TableHead>{t('scopeCalculationsEntriesCard.th_category')}</TableHead>
                      <TableHead className="text-right">{t('scopeCalculationsEntriesCard.th_quantity')}</TableHead>
                      <TableHead className="text-right">{t('scopeCalculationsEntriesCard.th_emissions')}</TableHead>
                      <TableHead>{t('scopeCalculationsEntriesCard.th_quality')}</TableHead>
                      <TableHead>{t('scopeCalculationsEntriesCard.th_status')}</TableHead>
                      <TableHead>{t('scopeCalculationsEntriesCard.th_created')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow
                        key={entry.id}
                        className={
                          selectionMode ? 'hover:bg-muted/30' : 'cursor-pointer hover:bg-muted/50'
                        }
                        onClick={() => {
                          onRowClick(entry);
                        }}
                      >
                        {selectionMode && (
                          <TableCell
                            className="w-12"
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            <Checkbox
                              checked={selectedEntryIds.includes(entry.id)}
                              onCheckedChange={(checked) => {
                                toggleEntrySelection(entry, Boolean(checked));
                              }}
                              disabled={entry.status === 'approved'}
                              title={
                                entry.status === 'approved'
                                  ? t('scopeCalculationsEntriesCard.approved_not_selectable')
                                  : undefined
                              }
                              aria-label={`Select ${getActivityLabel(entry)}`}
                            />
                          </TableCell>
                        )}
                        <TableCell>{getActivityLabel(entry)}</TableCell>
                        <TableCell>
                          {entry.facilityName?.trim() ? entry.facilityName.trim() : '-'}
                        </TableCell>
                        <TableCell>
                          {getActivityCategoryLabel(entry.activityCategory, t, {
                            scope3Category: entry.scope3Category,
                          })}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatQuantity(entry.quantity, entry.unit)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatEmissions(entry.estimatedCo2eKg)}
                        </TableCell>
                        <TableCell>
                          <DataQualityTierBadge quality={entry.dataQuality} />
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_BADGE_VARIANTS[entry.status]}>
                            {getStatusLabel(entry.status, t)}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(entry.createdAt).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    {t('scopeCalculationsEntriesCard.pagination_showing', { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, total), total })}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onPageChange(Math.max(1, page - 1));
                      }}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="mr-1 h-4 w-4" />
                      {t('scopeCalculationsEntriesCard.btn_previous')}
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {t('scopeCalculationsEntriesCard.pagination_page', { page, totalPages })}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onPageChange(page + 1);
                      }}
                      disabled={page >= totalPages}
                    >
                      {t('scopeCalculationsEntriesCard.btn_next')}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmBulkDeleteOpen} onOpenChange={setConfirmBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('scopeCalculationsEntriesCard.dialog_delete_title', { scope: scopeLabel })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('scopeCalculationsEntriesCard.dialog_delete_desc', { count: selectedOnPageCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePending}>{t('scopeCalculationsEntriesCard.btn_cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleBulkDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletePending || selectedOnPageCount === 0}
            >
              {deletePending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('scopeCalculationsEntriesCard.btn_delete_selected')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
